import { ClientEvent, type MatrixClient, type MatrixEvent, type SyncState } from 'matrix-js-sdk/src/matrix';

import logger from 'utils/logging/faro';

import { LandingSection, setDefaultLandingPage, USER_SETTINGS_ACCOUNT_DATA_KEY } from './setDefaultLandingPage';

/**
 * Module-level store that is the **single source of truth** for the user's
 * preferred landing page. Both `useDefaultLandingPage` (the post-login
 * navigation hook) and `LayoutTab` (the settings UI) read from this store
 * via `useSyncExternalStore`, so they can never disagree.
 *
 * Lifecycle:
 *  1. Store starts un-hydrated with the documented default ('messenger').
 *  2. First consumer to mount calls `hydrate(matrixClient)`.
 *  3. Hydration waits for `matrixClient.isInitialSyncComplete()` (subscribing
 *     to `ClientEvent.Sync` if not yet ready) before reading account data —
 *     this is the only deterministic API that account data is in the local
 *     store.
 *  4. After hydration, a `ClientEvent.AccountData` listener keeps the store
 *     in sync with external updates (e.g. preference changed on another
 *     device).
 *  5. On logout/login the `matrixClient` reference changes; the store
 *     detects this and re-hydrates against the new client.
 */

const VALID_SECTIONS: readonly LandingSection[] = ['messenger', 'calls', 'calendar'];
const DEFAULT_LANDING_PAGE: LandingSection = 'messenger';

export interface DefaultLandingPageState {
  /** Current preference. Defaults to `'messenger'` until hydration completes. */
  preference: LandingSection;
  /** True once the store has read the value from a fully synced Matrix client. */
  isHydrated: boolean;
}

const initialState: DefaultLandingPageState = {
  preference: DEFAULT_LANDING_PAGE,
  isHydrated: false,
};

let state: DefaultLandingPageState = initialState;
const listeners = new Set<() => void>();

/** The client this store is currently hydrated against — used to detect logout. */
let hydratedClient: MatrixClient | null = null;
let accountDataListener: ((event: MatrixEvent) => void) | null = null;
let syncListener: ((state: SyncState) => void) | null = null;

const emit = () => listeners.forEach((listener) => listener());

const setState = (next: DefaultLandingPageState) => {
  if (next.preference === state.preference && next.isHydrated === state.isHydrated) return;
  state = next;
  emit();
};

const readFromClient = (client: MatrixClient): LandingSection => {
  try {
    const accountData = client.getAccountData(USER_SETTINGS_ACCOUNT_DATA_KEY as never);
    const content = accountData?.getContent<{ defaultLandingPage?: string }>();
    const stored = content?.defaultLandingPage;
    if (stored && VALID_SECTIONS.includes(stored as LandingSection)) {
      return stored as LandingSection;
    }
  } catch (error) {
    logger.warn('defaultLandingPageStore: failed to read account data', error);
  }
  return DEFAULT_LANDING_PAGE;
};

const attachAccountDataListener = (client: MatrixClient) => {
  if (accountDataListener) return;
  accountDataListener = (event) => {
    if (event.getType() !== USER_SETTINGS_ACCOUNT_DATA_KEY) return;
    setState({ preference: readFromClient(client), isHydrated: true });
  };
  client.on(ClientEvent.AccountData, accountDataListener);
};

const detachAccountDataListener = (client: MatrixClient) => {
  if (!accountDataListener) return;
  client.off(ClientEvent.AccountData, accountDataListener);
  accountDataListener = null;
};

const detachSyncListener = (client: MatrixClient) => {
  if (!syncListener) return;
  client.off(ClientEvent.Sync, syncListener);
  syncListener = null;
};

const detachAllListeners = (client: MatrixClient) => {
  detachAccountDataListener(client);
  detachSyncListener(client);
};

const finishHydration = (client: MatrixClient) => {
  setState({ preference: readFromClient(client), isHydrated: true });
  attachAccountDataListener(client);
};

export const defaultLandingPageStore = {
  getSnapshot: (): DefaultLandingPageState => state,

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /**
   * Hydrate the store from the given client. Idempotent for the same client.
   * If the client identity changes (logout → login), detaches all listeners
   * from the previous client, resets state, then hydrates against the new one.
   */
  hydrate: (client: MatrixClient): void => {
    if (hydratedClient && hydratedClient !== client) {
      detachAllListeners(hydratedClient);
      hydratedClient = null;
      state = initialState;
      emit();
    }
    if (hydratedClient === client && state.isHydrated) return;

    hydratedClient = client;

    if (client.isInitialSyncComplete()) {
      finishHydration(client);
      return;
    }

    // Subscribe to sync and finish hydration once initial sync completes.
    // The listener self-detaches as soon as it succeeds so it never fires twice.
    syncListener = () => {
      if (!client.isInitialSyncComplete()) return;
      detachSyncListener(client);
      finishHydration(client);
    };
    client.on(ClientEvent.Sync, syncListener);
  },

  /**
   * Optimistically update the preference, then persist to Matrix account data.
   * Rolls back the store value on failure and re-throws so the caller can
   * surface an error toast.
   */
  set: async (client: MatrixClient, section: LandingSection): Promise<void> => {
    const previous = state.preference;
    setState({ preference: section, isHydrated: true });
    try {
      await setDefaultLandingPage(client, section);
    } catch (error) {
      setState({ preference: previous, isHydrated: true });
      throw error;
    }
  },

  /**
   * Reset the store to its initial state and detach all listeners. Intended
   * for tests; production logout is handled implicitly by the client-identity
   * check in `hydrate`.
   */
  reset: (): void => {
    if (hydratedClient) detachAllListeners(hydratedClient);
    hydratedClient = null;
    state = initialState;
    listeners.clear();
  },
};
